import { useQuery } from "@tanstack/react-query"

import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"

import { LoanDashboardHeader } from "@/screens/loans/components/dashboard/LoanDashboardHeader"
import { LoanStatusTracker } from "@/screens/loans/components/dashboard/LoanStatusTracker"
import { LoanTaskList } from "@/screens/loans/components/dashboard/LoanTaskList"
import { LoanSummarySection } from "@/screens/loans/components/dashboard/LoanSummarySection"
import { LoanArchiveAction } from "@/screens/loans/components/dashboard/LoanArchiveAction"

import LoanDashboardLayout from "@/screens/loans/components/dashboard/layouts/LoanDashboardLayout"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { ClosingDayBanner } from "./components/dashboard/LoanDashboardClosingDayBanner"

import {
  BorrowerDetailSection,
  ClosedLoanInfoSection,
  CompNotificationsSection,
  ContractDetailSection,
  LoanDetailsSection,
  MonthlyPaymentsSection,
  PropertyInfoSection,
  YourTeamSection,
} from "./components/dashboard/LoanDetails"
import { LoanTotalsSection } from "./components/dashboard/LoanTotalsSection"
import { LoanRehabSection } from "@/screens/loans/components/dashboard/LoanRehabSection"
import { usePostCloseFeature } from "@/hooks/features/usePostCloseFeature"
import { RehabBudgetDetails } from "@/screens/loans/components/dashboard/details/RehabBudgetDetails"
import { loanDashboardViewed } from "@/analytics/events/loans"
import { useBoolVariation } from "@/hooks/launchDarklyHooks"
import { LoanStatusCard } from "@/screens/loans/components/dashboard/LoanStatusCard"
import { LoanRequestsAction } from "@/screens/loans/components/LoanRequestsAction"
import { TrustedPartnersAction } from "@/screens/loans/components/TrustedPartnersAction"
import { MilestoneList } from "@/screens/loans/components/dashboard/milestones"

function PreSubmissionScreen({ loanId }: { loanId: string }) {
  const v1dash = useBoolVariation("loan-dashboard-v1", false)

  if (v1dash) {
    return (
      <LoanDashboardLayout>
        <LoanDashboardHeader loanId={loanId} />
        <MilestoneList loanId={loanId} />
        <TrustedPartnersAction loanId={loanId} />
        <LoanArchiveAction loanId={loanId} />
      </LoanDashboardLayout>
    )
  }

  return (
    <LoanDashboardLayout>
      <LoanDashboardHeader loanId={loanId} />
      <LoanTaskList loanId={loanId} />
      <LoanStatusTracker loanId={loanId} />
      <LoanArchiveAction loanId={loanId} />
    </LoanDashboardLayout>
  )
}

function PreClosingScreen({ loanId }: { loanId: string }) {
  const v1dash = useBoolVariation("loan-dashboard-v1", false)

  if (v1dash) {
    return (
      <LoanDashboardLayout>
        <LoanDashboardHeader loanId={loanId} />
        <LoanStatusCard loanId={loanId} />
        <MilestoneList loanId={loanId} />
        <LoanRequestsAction loanId={loanId} />
        <TrustedPartnersAction loanId={loanId} />
      </LoanDashboardLayout>
    )
  }

  return (
    <LoanDashboardLayout>
      <LoanDashboardHeader loanId={loanId} />
      <LoanTaskList loanId={loanId} />
      <LoanStatusTracker loanId={loanId} />
      <LoanRehabSection loanId={loanId} />
      <LoanSummarySection loanId={loanId} />
      <LoanArchiveAction loanId={loanId} />
    </LoanDashboardLayout>
  )
}

function ClosingDayScreen({ loanId }: { loanId: string }) {
  return (
    <LoanDashboardLayout>
      <LoanDashboardHeader loanId={loanId} />
      <ClosingDayBanner />
      <LoanStatusTracker loanId={loanId} />
      <LoanRehabSection loanId={loanId} />
      <LoanSummarySection loanId={loanId} />
      <LoanArchiveAction loanId={loanId} />
    </LoanDashboardLayout>
  )
}

function FundedScreen({ loanId }: { loanId: string }) {
  return (
    <LoanDashboardLayout>
      <LoanDashboardHeader loanId={loanId} />
      <BorrowerDetailSection loanId={loanId} />
      <ContractDetailSection loanId={loanId} />
      <PropertyInfoSection loanId={loanId} />
      <LoanDetailsSection loanId={loanId} />
      <LoanTotalsSection loanId={loanId} />
    </LoanDashboardLayout>
  )
}

function PostCloseScreen({ loanId }: { loanId: string }) {
  return (
    <LoanDashboardLayout>
      <LoanDashboardHeader loanId={loanId} />
      <MonthlyPaymentsSection loanId={loanId} />
      <RehabBudgetDetails loanId={loanId} />
      <YourTeamSection loanId={loanId} />
      <ClosedLoanInfoSection loanId={loanId} />
      <CompNotificationsSection loanId={loanId} />
    </LoanDashboardLayout>
  )
}

export default function LoanDashboardScreen({ loanId }: { loanId: string }) {
  const dashboardQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return {
        timelineStage: data?.timelineStage!,
      }
    },
  })

  const { timelineStage } = dashboardQuery.data || {}

  const { postCloseFeatureEnabled } = usePostCloseFeature(loanId)

  useViewedCallback(() => {
    setStatusBarStyle("light")
    if (!timelineStage) {
      return
    }
    console.log("Viewed Loan Dashboard", loanId, timelineStage.value)
    loanDashboardViewed({ loanState: timelineStage.value, loanId })
  }, [loanId, timelineStage])

  if (!timelineStage) {
    return null
  }

  if (timelineStage.value === "current") {
    if (postCloseFeatureEnabled) {
      return <PostCloseScreen loanId={loanId} />
    } else {
      return <FundedScreen loanId={loanId} />
    }
  }

  if (timelineStage.value === "repaid") {
    return <FundedScreen loanId={loanId} />
  }

  if (timelineStage.value === "closing_day") {
    return <ClosingDayScreen loanId={loanId} />
  }

  if (timelineStage.value === "pre_submit") {
    return <PreSubmissionScreen loanId={loanId} />
  }

  // timelineStage.value === "submitted"
  return <PreClosingScreen loanId={loanId} />
}
